import React, { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Icon,
  Loader,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';

import useCreateRule from '../../../hooks/use-createrule';
import useDeleteRule from '../../../hooks/use-deleterule';
import useGetSampleUsers from '../../../hooks/use-getsampleusers';
import useUpdateRule from '../../../hooks/use-updaterule';
import {
  type Condition,
  type Rule,
  type UserData,
} from '../../../services/types';
import messages from '../../../shared/messages';
import { isEmpty, useInvertedLocalize } from '../../../utils';
import { OPERATORS_WITH_NO_VALIDATION } from '../../../utils/constants';
import { useLocalize } from '../../../utils/localization';
import RuleCondition from '../../RuleCondition/RuleCondition';

import fixedColumns from './constants';
import invertedMessages from './messages';

import './TabContent.css';

interface TabContentProps {
  rule: Rule;
  isNewRule: boolean;
  refetchRules: () => void;
  resetActiveTab: () => void;
  setIsNewRuleAdded: (key: boolean) => void;
  setNewRuleName: (key: string) => void;
  setErrorMessage: (key: string) => void;
  setShowRemoveSuccessBanner: (key: boolean) => void;
  setShowCriticalBanner: (key: boolean) => void;
}

function TabContent({
  rule,
  isNewRule,
  refetchRules,
  resetActiveTab,
  setIsNewRuleAdded, // setActiveTabToNew,
  setNewRuleName,
  setShowRemoveSuccessBanner,
  setErrorMessage,
  setShowCriticalBanner,
}: TabContentProps) {
  const [updatedCondition, setUpdatedCondition] = useState<Condition>(
    rule.conditions[0]
  );
  const [updatedRule, setUpdatedRule] = useState(rule);
  const [isRuleConfirmed, setIsRuleConfirmed] = useState(true);
  const [isRemoveModal, setIsRemoveModal] = useState(false);
  const [showSampleUsers, setShowSampleUsers] = useState(false);
  const [sampleUsers, setSampleUsers] = useState<UserData[]>();
  const [conditionValue, setConditionValue] = useState<string>('');
  const [isCreateSuccess, setIsCreateSuccess] = useState<boolean>(false);
  const [isUpdateRuleSuccess, setIsUpdateRuleSuccess] =
    useState<boolean>(false);

  const isRecommendedRule = rule.status === 2;

  const l = useInvertedLocalize(invertedMessages);
  const localize = useLocalize();

  const {
    mutate: getSampleUsers,
    isSuccess: isGetSampleUsersSuccess,
    isPending: isGetSampleUsersPending,
  } = useGetSampleUsers();
  const { mutate: updateRule, isPending: isRuleUpdating } = useUpdateRule();
  const {
    mutate: deleteRule,
    isSuccess: isDeleteSuccess,
    isPending: isRuleDeleting,
  } = useDeleteRule();
  const {
    mutateAsync: createRule,
    data: createdRuleData,
    isPending,
  } = useCreateRule();

  useEffect(() => {
    if (isUpdateRuleSuccess) {
      refetchRules();
    }
  }, [isUpdateRuleSuccess, refetchRules]);

  useEffect(() => {
    if (isGetSampleUsersSuccess && sampleUsers?.length) {
      setShowSampleUsers(true);
    }
  }, [isGetSampleUsersSuccess, sampleUsers]);

  useEffect(() => {
    if (isCreateSuccess) {
      refetchRules();
      setIsNewRuleAdded(false);
    }
  }, [isCreateSuccess, refetchRules, createdRuleData, setIsNewRuleAdded]);

  useEffect(() => {
    if (isDeleteSuccess) {
      refetchRules();
      resetActiveTab();
    }
  }, [isDeleteSuccess, refetchRules, resetActiveTab]);

  useEffect(() => {
    setUpdatedRule((prevRule) => {
      const ruleDescription = Array.from(
        `If users ${
          updatedCondition.attributeLabel ?? updatedCondition.attribute
        } ${updatedCondition.operator} ${updatedCondition.values.join(', ')}`
      )
        .slice(0, 255)
        .join('');

      return {
        ...prevRule,
        ruleDescription,
      };
    });
  }, [updatedCondition]);

  useEffect(() => {
    setUpdatedCondition(rule.conditions[0]);
  }, [rule.conditions]);

  const handleConfirmBtnClick = async () => {
    if (isNewRule) {
      delete updatedRule.ruleKey;
      delete updatedCondition.attributeKey;
      updatedRule.name = `EXTERNAL_${new Date().toISOString()}`;
      updatedRule.conditions = [updatedCondition];
      updatedRule.status = 0;
      await createRule(
        { ...updatedRule },
        {
          onSuccess: (response, variables) => {
            if (response.status === 200 || response.status === 201) {
              setNewRuleName(variables.name);
              setIsRuleConfirmed(true);
              setErrorMessage('');
              setIsCreateSuccess(true);
            } else if (response.errorMessage) {
              if (response.error?.validationErrors) {
                const errMsg = response.errorMessage;
                const errReason = Object.values(
                  response.error.validationErrors
                ).join(', ');

                setErrorMessage(`${errMsg}: ${errReason} `);
              } else {
                setErrorMessage(response.errorMessage);
              }

              setIsCreateSuccess(false);
              setIsRuleConfirmed(false);
              setShowCriticalBanner(true);
            }
          },
        }
      );
    } else {
      updateRule(
        {
          ...rule,
          status: 0,
          ruleDescription: updatedRule.ruleDescription,
          conditions: [updatedCondition],
        },
        {
          onError: (response) => {
            setErrorMessage(response.message);
            setIsRuleConfirmed(false);
            setShowCriticalBanner(true);
          },
          onSuccess: (response) => {
            if (response.errorMessage) {
              if (response.error?.validationErrors) {
                const errMsg = response.errorMessage;
                const errReason = Object.values(
                  response.error.validationErrors
                ).join(', ');

                setErrorMessage(`${errMsg}: ${errReason} `);
              } else {
                setErrorMessage(response.errorMessage);
              }

              setIsUpdateRuleSuccess(false);
              setIsRuleConfirmed(false);
              setShowCriticalBanner(true);
            } else {
              setIsRuleConfirmed(true);
              setIsUpdateRuleSuccess(true);
              setErrorMessage('');
            }
          },
        }
      );
    }
  };

  const runShowSampleUsers = useCallback(() => {
    getSampleUsers(rule.ruleKey ?? 0, {
      onSuccess: (response) => {
        if (response.status === 200 || response.status === 201 || response.ok) {
          setSampleUsers(response.data?.usersData);
          setErrorMessage('');
        } else if (response.errorMessage || response.error) {
          setErrorMessage(response.errorMessage ?? '');
          setShowCriticalBanner(true);
        }
      },
    });
  }, [rule.ruleKey, getSampleUsers, setShowCriticalBanner, setErrorMessage]);

  useEffect(() => {
    if (isRecommendedRule) {
      runShowSampleUsers();
    }
  }, [isRecommendedRule, runShowSampleUsers]);

  const showSampleUsersBtn =
    !showSampleUsers && !isNewRule && !isGetSampleUsersPending;

  const isConditionValueEmpty = !OPERATORS_WITH_NO_VALIDATION.includes(
    updatedCondition.operator
  )
    ? isEmpty(updatedCondition.values) && isEmpty(conditionValue)
    : false;

  const isConfirmBtnDisabled =
    isEmpty(updatedCondition.attribute) ||
    isEmpty(updatedCondition.operator) ||
    isConditionValueEmpty ||
    isPending ||
    isRuleUpdating;

  // Extract dynamic column names from the attributes array
  const dynamicColumns = new Set<string>(
    sampleUsers?.flatMap((user: UserData) =>
      user.attributes.map((attr) => attr.label)
    )
  );

  // Ensure fixed columns come first, followed by any additional fields
  const sortedColumns: string[] = [
    ...fixedColumns,
    ...[...dynamicColumns].filter((col) => !fixedColumns.includes(col)),
  ];

  // Format the data
  const formattedData = sampleUsers?.map((user: UserData) => {
    const formattedUser: Record<string, string | number | null> = {};

    sortedColumns.forEach((col) => {
      const attribute = user.attributes.find((attr) => attr.label === col);

      formattedUser[col] =
        attribute && !isEmpty(attribute.value) ? attribute.value ?? '-' : '-';
    });

    return formattedUser;
  });

  const getStatus = useCallback(
    (status: number) => {
      switch (status) {
        case 1:
          return l('Active');
        case 0:
          return l('Inactive');
        case 7:
          return l(`Suspended From Import Service`);
        default:
          return '-';
      }
    },
    [l]
  );

  return (
    <div className='Ext-RuleTabs-content-ruleDetails'>
      <ModalIsland
        kind='warning'
        alertTitle={l('Confirm to remove rule?')}
        alertSubtitle={l(
          'This action cannot be undone. The selected rule will be removed from the list.'
        )}
        onClose={() => setIsRemoveModal(false)}
        isOpen={isRemoveModal}
        primaryButton={
          <Button
            type='button'
            kind='outlined'
            size='medium'
            leftIcon={<Icon kind='Delete' size='medium' color='neutral-100' />}
            isLoading={isRuleDeleting}
            loaderText='Removing'
            isDisabled={isRuleDeleting}
            onClick={() => {
              deleteRule(
                {
                  ...rule,
                  conditions: [updatedCondition],
                },
                {
                  onSuccess: (response) => {
                    if (response.status === 200 || response.status === 201) {
                      setShowRemoveSuccessBanner(true);
                      setIsRemoveModal(false);
                      setErrorMessage('');
                    } else if (response.errorMessage || response.error) {
                      setErrorMessage(response.errorMessage ?? '');
                      setShowCriticalBanner(true);
                    }
                  },
                }
              );
            }}>
            {l('Remove')}
          </Button>
        }
        secondaryButton={
          <Button
            type='button'
            kind='ghost'
            size='medium'
            onClick={() => {
              setIsRemoveModal(false);
            }}>
            {l('Cancel')}
          </Button>
        }
      />
      <Typography kind='h3' color='--color-foreground-neutral-bold'>
        {showSampleUsersBtn
          ? l('Review Rule and Make Changes if necessary')
          : l('Create New Rule')}
      </Typography>
      <RuleCondition
        // rule={rule}
        ruleCondition={rule.conditions}
        setUpdatedCondition={setUpdatedCondition}
        setIsRuleConfirmed={setIsRuleConfirmed}
        setConditionValue={setConditionValue}
      />
      <div className='Ext-RuleTabs-content-actions'>
        {!isNewRule && (
          <Button
            type='button'
            kind='outlined'
            size='medium'
            leftIcon={<Icon kind='Delete' size='medium' color='neutral-100' />}
            onClick={() => {
              setIsRemoveModal(true);
            }}>
            {l('Remove')}
          </Button>
        )}
        {rule.status === 0 && isRuleConfirmed ? (
          <Button
            type='button'
            kind='filled'
            size='medium'
            isSuccess
            successText={l('Confirmed')}
            leftIcon={<Icon kind='Check' />}>
            {l('Confirmed')}
          </Button>
        ) : (
          <Button
            type='button'
            kind='filled'
            size='medium'
            isLoading={isPending || isRuleUpdating}
            loaderText='Confirming'
            isDisabled={isConfirmBtnDisabled}
            onClick={async () => {
              await handleConfirmBtnClick();
            }}>
            {l('Confirm')}
          </Button>
        )}
      </div>
      {showSampleUsersBtn ? (
        <div className='Ext-RuleTabs-content-sampleUsers'>
          <Button
            type='button'
            kind='ghost'
            size='medium'
            leftIcon={
              <Icon kind='ArrowDown' size='medium' color='neutral-100' />
            }
            className='Ext-RuleTabs-content-sampleUsers-btn'
            onClick={runShowSampleUsers}>
            {l('Show User Samples')}
          </Button>
        </div>
      ) : (
        ''
      )}
      {isGetSampleUsersPending && (
        <div className='Ext-RuleTabs-content-sampleUsers Ext-RuleTabs-content-ruleDetails--isLoading'>
          <Loader
            kind='loop'
            format='inline'
            color='brand'
            className='Ext-RuleTabs-content-ruleDetails-loader'
          />
          <div className='Ext-RuleTabs-content-sampleUsers-btn'>
            <Typography
              kind='body2-bold'
              color='--color-foreground-primary-medium'>
              {l('Loading sample users')}
            </Typography>
          </div>
        </div>
      )}
      {!isEmpty(sampleUsers)
        ? showSampleUsers && (
            <div className='Ext-RuleTabs-content-sampleUsers Ext-RuleTabs-content-ruleDetails-usersContainer'>
              <div className='Ext-RuleTabs-content-ruleDetails-usersContainer-title'>
                <Typography kind='body2'>
                  {l('Based on the rule, here are some user data sample:')}
                </Typography>
              </div>
              {formattedData?.map((user) => (
                <Box className='Ext-RuleTabs-content-ruleDetails-usersContainer-user'>
                  <Avatar size='small' />
                  <div className='Ext-RuleTabs-content-ruleDetails-usersContainer-userCol'>
                    <Typography kind='body3'>
                      {localize({
                        id: 'Users.username.label',
                        defaultMessage: messages['Users.username.label'],
                      })}
                    </Typography>
                    <Typography kind='h3' lineLimit={1}>
                      {user['Users.username.label']}
                    </Typography>
                  </div>
                  <div className='Ext-RuleTabs-content-ruleDetails-usersContainer-userCol'>
                    <Typography kind='body3'>
                      {localize({
                        id: 'Users.firstname.label',
                        defaultMessage: messages['Users.firstname.label'],
                      })}
                    </Typography>
                    <Typography kind='body2' lineLimit={1}>
                      {user['Users.firstname.label']}{' '}
                    </Typography>
                  </div>
                  <div className='Ext-RuleTabs-content-ruleDetails-usersContainer-userCol'>
                    <Typography kind='body3'>
                      {localize({
                        id: 'Users.lastname.label',
                        defaultMessage: messages['Users.lastname.label'],
                      })}
                    </Typography>
                    <Typography kind='body2' lineLimit={1}>
                      {user['Users.lastname.label']}
                    </Typography>
                  </div>
                  <div className='Ext-RuleTabs-content-ruleDetails-usersContainer-userCol'>
                    <Typography kind='body3'>
                      {localize({
                        id: 'Users.statuskey.label',
                        defaultMessage: messages['Users.statuskey.label'],
                      })}
                    </Typography>
                    <Typography kind='body2'>
                      {getStatus(Number(user['Users.statuskey.label']))}
                    </Typography>
                  </div>
                  {/* Dynamically Render Additional Fields */}
                  {sortedColumns
                    .filter((col) => !fixedColumns.includes(col)) // Only extra fields
                    .map((col) => (
                      <div className='Ext-RuleTabs-content-ruleDetails-usersContainer-userCol'>
                        <Typography kind='body3'>
                          {localize({
                            id: col,
                            defaultMessage: messages[col],
                          })}
                        </Typography>
                        <Typography kind='body2' lineLimit={1}>
                          {user[col]}
                        </Typography>
                      </div>
                    ))}
                </Box>
              ))}
            </div>
          )
        : isGetSampleUsersSuccess && (
            <div className='Ext-RuleTabs-content-ruleDetails-noUsers'>
              {l('No matching users.')}
            </div>
          )}
      {sampleUsers?.length && showSampleUsers ? (
        <div className=''>
          <Button
            type='button'
            kind='ghost'
            size='medium'
            leftIcon={<Icon kind='ArrowUp' size='medium' color='neutral-100' />}
            onClick={() => {
              setShowSampleUsers(false);
            }}>
            {l('Hide User Samples')}
          </Button>
        </div>
      ) : (
        ''
      )}
    </div>
  );
}

export default TabContent;
